import { GridPaginationModel } from "@mui/x-data-grid-pro";

interface GetTotalPagesProps extends Pick<GridPaginationModel, "pageSize"> {
  /** The total number of items that are being paginated. */
  totalItems: number;
}

/**
 * Defines the total number of pages based on the total number of items and the page size.
 */
export function getTotalPages({
  totalItems,
  pageSize,
}: GetTotalPagesProps): number {
  return Math.ceil(totalItems / pageSize);
}

/**
 * Defines the start items based on the page size and the current page.
 */
export function getStartItems({ pageSize, page }: GridPaginationModel): number {
  return pageSize * page + 1;
}

interface GetEndItemsProps extends GridPaginationModel {
  /** The total number of items that are being paginated. */
  totalItems: number;

  /** The total number of pages. */
  totalPages: number;
}

/**
 * Defines the end items based on the page size, the current page,
 * the total number of items, and the total number of pages.
 */
export function getEndItems({
  pageSize,
  page,
  totalItems,
  totalPages,
}: GetEndItemsProps): number {
  // If we are in the last page, we show the total of items as end items,
  // otherwise could say 21-30 of 25.
  if (totalPages === page + 1) {
    return totalItems;
  }

  return pageSize * (page + 1);
}

/**
 * Calculates the index of the last page based on the total row count and items per page.
 * Returns -1 if the row count is unknown (when using server-side pagination with unknown total).
 * Note: this function can only be used for the server side pagination of CoreAPI paginated endpoint.
 *
 * @param rowCount The total number of rows (or -1 if unknown)
 * @param itemsPerPage The number of items displayed per page
 * @returns The last page index (0-based) or -1 if the row count is unknown
 */
export function getLastPage(rowCount: number, itemsPerPage: number): number {
  if (rowCount === -1) {
    return -1;
  }

  // Special case for rowCount === 0, return 0 (first page) as there's no items
  if (rowCount === 0) {
    return 0;
  }

  const quotient = Math.floor(rowCount / itemsPerPage);
  const remainder = rowCount % itemsPerPage;

  return remainder === 0 ? quotient - 1 : quotient;
}
